import _ from 'lodash'
import type { Component, ComponentQueries, QueryProperty, ViewMode } from '@wix/thunderbolt-becky-types'
import { pointers } from '@wix/thunderbolt-becky-types'
import { getUniqueId } from '@wix/thunderbolt-commons'
import type { RawModel } from '../../types'
import { SITE_NAMESPACE, SYSTEM_STYLES, QUERY_MAPPING } from '../constants'
import { DATA_QUERIES_TO_INFLATE, getUniqueDataQuery, isMobileProperty } from '../../utils'
import { hasRemoteOverride, hasSiteOverride, resolveSkin } from '../byRefUtils'

const PROPERTY_TYPES = pointers.components.PROPERTY_TYPES

export const getIsSystemStyle = (templateComp: Component, rawModel: RawModel) => {
	return templateComp.styleId && _.get(rawModel, [SITE_NAMESPACE, SYSTEM_STYLES, templateComp.styleId])
}

const inflateQueries = (rawModel: RawModel, templateComp: Component, inflatedCompId: string, viewMode: ViewMode) => {
	const isSystemStyle = getIsSystemStyle(templateComp, rawModel)

	const inflatedQueries = DATA_QUERIES_TO_INFLATE.reduce((acc, dataQueryName) => {
		const withMobileSuffix = isMobileProperty(viewMode, dataQueryName)
		// if templateComp style points to a system style in masterPage, styleId data query should remain unchanged
		if (dataQueryName === PROPERTY_TYPES.STYLE_ID && isSystemStyle) {
			acc[dataQueryName as QueryProperty] = templateComp[dataQueryName as QueryProperty]
		} else if (
			templateComp[dataQueryName as QueryProperty] ||
			hasRemoteOverride(rawModel, dataQueryName, inflatedCompId) ||
			hasSiteOverride(rawModel, QUERY_MAPPING[dataQueryName as QueryProperty], inflatedCompId)
		) {
			acc[dataQueryName as QueryProperty] = getUniqueDataQuery(inflatedCompId, dataQueryName, withMobileSuffix)
		}
		return acc
	}, {} as ComponentQueries)
	return inflatedQueries
}

export const inflateCompStructure = ({
	pageId,
	rawModel,
	templateComp,
	ancestorsChain,
	isRootComp,
	viewMode,
}: {
	rawModel: RawModel
	templateComp: Component
	ancestorsChain: string
	pageId: string
	isRootComp: boolean
	viewMode: ViewMode
}): Component => {
	const templateCompId = templateComp.id
	const inflatedCompId = getUniqueId(ancestorsChain, templateCompId)
	return {
		...templateComp,
		...inflateQueries(rawModel, templateComp, inflatedCompId, viewMode),
		...(templateComp.skin ? { skin: resolveSkin(templateComp.skin) } : {}),
		templateId: templateCompId,
		id: inflatedCompId,
		metaData: {
			...templateComp.metaData,
			pageId,
		},
		parent: isRootComp ? ancestorsChain : getUniqueId(ancestorsChain, templateComp.parent as string),
		components: templateComp.components
			? templateComp.components.map((childCompId) => getUniqueId(ancestorsChain, childCompId))
			: undefined,
	}
}
